import type {
  CancelRestockWaitlistDTO,
  CreateRestockWaitlistDTO,
  RestockWaitlistListQueryDTO,
  UpdateRestockWaitlistDTO,
} from "@/modules/restock-waitlist/domain/contracts";
import type {
  RestockWaitlistDetailDTO,
  RestockWaitlistImportHintDTO,
  RestockWaitlistStatusCountsDTO,
} from "@/modules/restock-waitlist/domain/types";
import type { OffsetPageDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type RestockWaitlistListResult = OffsetPageDTO<
  import("@/modules/restock-waitlist/domain/types").RestockWaitlistListItemDTO
>;

export const restockWaitlistApi = {
  async counts(): Promise<RestockWaitlistStatusCountsDTO> {
    return fetcher.get(`${config.apiUrl}/restock-waitlist/meta/counts`);
  },
  async previewImport(variantId: string): Promise<RestockWaitlistImportHintDTO> {
    return fetcher.get(
      `${config.apiUrl}/restock-waitlist/preview-import/${variantId}`,
    );
  },
  async list(query?: RestockWaitlistListQueryDTO): Promise<RestockWaitlistListResult> {
    const url = new URL(`${config.apiUrl}/restock-waitlist`);
    if (query?.status?.length) {
      url.searchParams.set("status", query.status.join(","));
    }
    if (query?.variantId) url.searchParams.set("variantId", query.variantId);
    if (query?.customerId) url.searchParams.set("customerId", query.customerId);
    if (query?.q) url.searchParams.set("q", query.q);
    url.searchParams.set("limit", String(query?.limit ?? 20));
    url.searchParams.set("offset", String(query?.offset ?? 0));
    return fetcher.get(url.toString());
  },
  async get(id: string): Promise<RestockWaitlistDetailDTO> {
    return fetcher.get(`${config.apiUrl}/restock-waitlist/${id}`);
  },
  async getNotifyMessage(id: string): Promise<{ message: string }> {
    return fetcher.get(`${config.apiUrl}/restock-waitlist/${id}/notify-message`);
  },
  async create(input: CreateRestockWaitlistDTO): Promise<RestockWaitlistDetailDTO> {
    return fetcher.post(`${config.apiUrl}/restock-waitlist`, input);
  },
  async update(
    id: string,
    input: UpdateRestockWaitlistDTO,
  ): Promise<RestockWaitlistDetailDTO> {
    return fetcher.patch(`${config.apiUrl}/restock-waitlist/${id}`, input);
  },
  async notify(id: string): Promise<RestockWaitlistDetailDTO> {
    return fetcher.post(`${config.apiUrl}/restock-waitlist/${id}/notify`, {});
  },
  async cancel(
    id: string,
    input?: CancelRestockWaitlistDTO,
  ): Promise<RestockWaitlistDetailDTO> {
    return fetcher.post(
      `${config.apiUrl}/restock-waitlist/${id}/cancel`,
      input ?? {},
    );
  },
};
